
import { useEffect, useMemo, useState } from "react";
import {
  DEFAULT_TEMPLATE_TYPE,
  TEMPLATE_TYPES,
  templateTypeLabel,
} from "@/lib/mapper/template-types";
import { marketplaceIcon } from "@/components/icons/brands";
import type { ProductType, Profile } from "@/lib/mapper/types";

export type SaveTemplateMode = "new" | "overwrite";

export type SaveTemplateProfileSummary = {
  id: string;
  name: string;
  productType: ProductType;
  type: string;
  isDefault: boolean;
  config: Profile;
};

type Props = {
  open: boolean;
  initialMode: SaveTemplateMode;
  profile: Profile;
  profiles: SaveTemplateProfileSummary[];
  loadedProfileId: string | null;
  saving: boolean;
  onClose: () => void;
  onCreate: (args: { name: string; type: string }) => Promise<void>;
  onOverwrite: (profileId: string) => Promise<void>;
};

export function SaveTemplateModal({
  open,
  initialMode,
  profile,
  profiles,
  loadedProfileId,
  saving,
  onClose,
  onCreate,
  onOverwrite,
}: Props) {
  const [mode, setMode] = useState<SaveTemplateMode>(initialMode);
  const [name, setName] = useState(`${profile.productType} template`);
  const [type, setType] = useState(DEFAULT_TEMPLATE_TYPE);
  const [overwriteId, setOverwriteId] = useState("");
  const [error, setError] = useState<string | null>(null);

  const sortedProfiles = useMemo(
    () =>
      [...profiles].sort(
        (a, b) =>
          templateTypeLabel(a.type).localeCompare(templateTypeLabel(b.type)) ||
          a.name.localeCompare(b.name),
      ),
    [profiles],
  );

  useEffect(() => {
    if (!open) return;
    setMode(initialMode);
    setName(`${profile.productType} template`);
    setType(DEFAULT_TEMPLATE_TYPE);
    setOverwriteId(loadedProfileId ?? sortedProfiles[0]?.id ?? "");
    setError(null);
  }, [initialMode, loadedProfileId, open, profile.productType, sortedProfiles]);

  if (!open) return null;

  const canOverwrite = sortedProfiles.length > 0 && overwriteId.length > 0;
  const canSubmit = mode === "new" ? name.trim().length > 0 : canOverwrite;
  const selectedOverwriteType =
    sortedProfiles.find((item) => item.id === overwriteId)?.type ??
    DEFAULT_TEMPLATE_TYPE;
  const TypeIcon = marketplaceIcon(
    mode === "new" ? type : selectedOverwriteType,
  );

  async function submit() {
    if (!canSubmit || saving) return;
    setError(null);
    try {
      if (mode === "new") {
        await onCreate({ name: name.trim(), type });
      } else {
        await onOverwrite(overwriteId);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed");
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/30 px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="save-template-title"
    >
      <div className="w-full max-w-lg rounded-lg border border-slate-200 dark:border-border bg-white dark:bg-surface shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 dark:border-border px-5 py-4">
          <div>
            <h2
              id="save-template-title"
              className="text-base font-semibold text-slate-950 dark:text-slate-100"
            >
              Save Template
            </h2>
            <p className="mt-1 text-[12px] text-slate-500 dark:text-slate-400">
              Save this workbook mapping as a new template or overwrite an
              existing one.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded border border-slate-200 dark:border-border px-2 py-1 text-[12px] text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-surface-2 disabled:opacity-50"
          >
            Close
          </button>
        </div>

        <div className="px-5 py-4">
          <div className="inline-flex rounded border border-slate-200 dark:border-border bg-slate-50 dark:bg-background p-0.5">
            <button
              type="button"
              onClick={() => setMode("new")}
              className={[
                "rounded px-3 py-1 text-[12px] font-medium transition",
                mode === "new"
                  ? "bg-white dark:bg-surface text-slate-950 dark:text-slate-100 shadow-sm"
                  : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100",
              ].join(" ")}
            >
              New template
            </button>
            <button
              type="button"
              onClick={() => setMode("overwrite")}
              disabled={sortedProfiles.length === 0}
              className={[
                "rounded px-3 py-1 text-[12px] font-medium transition",
                mode === "overwrite"
                  ? "bg-white dark:bg-surface text-slate-950 dark:text-slate-100 shadow-sm"
                  : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100",
                sortedProfiles.length === 0 ? "opacity-40" : "",
              ].join(" ")}
            >
              Overwrite existing
            </button>
          </div>

          {mode === "new" ? (
            <div className="mt-4 grid gap-3">
              <label className="block">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Name
                </span>
                <input
                  type="text"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  className="mt-1 w-full rounded border border-slate-300 dark:border-border bg-white dark:bg-surface px-3 py-2 text-sm text-slate-950 dark:text-slate-100 focus:border-slate-500 dark:focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-300 dark:focus:ring-slate-600"
                />
              </label>
              <label className="block">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Type
                </span>
                <span className="mt-1 flex items-center gap-2 rounded border border-slate-300 bg-white px-3 dark:border-border dark:bg-surface">
                  <TypeIcon
                    size={16}
                    className="text-slate-600 dark:text-slate-300"
                  />
                  <select
                    value={type}
                    onChange={(event) => setType(event.target.value)}
                    className="min-w-0 flex-1 bg-transparent py-2 text-sm text-slate-950 focus:outline-none dark:text-slate-100"
                  >
                    {TEMPLATE_TYPES.map((templateType) => (
                      <option key={templateType.id} value={templateType.id}>
                        {templateType.label}
                      </option>
                    ))}
                  </select>
                </span>
              </label>
            </div>
          ) : (
            <label className="mt-4 block">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Template
              </span>
              <span className="mt-1 flex items-center gap-2 rounded border border-slate-300 bg-white px-3 dark:border-border dark:bg-surface">
                <TypeIcon
                  size={16}
                  className="text-slate-600 dark:text-slate-300"
                />
                <select
                  value={overwriteId}
                  onChange={(event) => setOverwriteId(event.target.value)}
                  className="min-w-0 flex-1 bg-transparent py-2 text-sm text-slate-950 focus:outline-none dark:text-slate-100"
                >
                  {sortedProfiles.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name} - {templateTypeLabel(item.type)}
                    </option>
                  ))}
                </select>
              </span>
            </label>
          )}

          {error ? (
            <p className="mt-3 text-[12px] text-red-600 dark:text-red-400">
              {error}
            </p>
          ) : null}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-slate-200 dark:border-border px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded border border-slate-300 dark:border-border bg-white dark:bg-surface px-3 py-1.5 text-[12px] font-medium text-slate-700 dark:text-slate-300 transition hover:bg-slate-50 dark:hover:bg-surface-2 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!canSubmit || saving}
            className="rounded bg-slate-900 dark:bg-slate-100 dark:text-slate-950 px-3 py-1.5 text-[12px] font-medium text-white transition hover:bg-slate-700 dark:hover:bg-slate-200 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? "Saving..." : "Save Template"}
          </button>
        </div>
      </div>
    </div>
  );
}
